(() => {
  if (customElements.get('df-combo-posts')) return;
  const combinations = [
    { service: '0', post: '0', value: 'Select Option' },
    { service: '1', post: '2', value: 'Installed' },
    { service: '2', post: '2', value: 'Supply Only' },
    { service: '1', post: '0', value: 'Installed - Select Option (posts)' },
    { service: '2', post: '0', value: 'Supply Only - Select Option (posts)' },
    { service: '0', post: '1', value: 'Select Option - No posts needed' },
    { service: '1', post: '1', value: 'Installed - No posts needed' },
    { service: '2', post: '1', value: 'Supply Only - No posts needed' },
    { service: '0', post: '2', value: 'Select Option - Heavy Duty Concrete Intermediate posts' }
  ];
  const byPair = new Map(combinations.map(item => [item.service + ':' + item.post, item]));
  const byValue = new Map(combinations.map(item => [item.value, item]));
  const prompt = 'Please choose all four options before adding to your basket.';
  const failure = 'The price could not be confirmed. Please refresh the page before ordering.';
  class DFComboPosts extends HTMLElement {
    static combinedValue(service, post) { return byPair.get(service + ':' + post)?.value ?? null; }
    static logicalValues(value) {
      const item = byValue.get(value);
      return item ? { service: item.service, post: item.post } : null;
    }
    connectedCallback() {
      if (this._connected) return;
      this._connected = true;
      this._ready = false;
      this._pending = false;
      this._fatal = false;
      this._expected = null;
      this._candidateVariant = null;
      this._lockedControls = new Map();
      this._gateStates = new Map();
      this._scope = this.closest('product-info') || this.closest('[id^="MainProduct-"]') || this.parentElement;
      this._onChangeBound = this._onChange.bind(this);
      this._onSubmitBound = this._onSubmit.bind(this);
      this.addEventListener('change', this._onChangeBound, true);
      this._observer = new MutationObserver(() => this._queueReconcile());
      this._observer.observe(this._scope, { childList: true, subtree: true });
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this._unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, event => {
          const data = event?.data || event;
          const sectionId = this._sectionId();
          if (data?.sectionId && sectionId && String(data.sectionId) !== sectionId) return;
          this._candidateVariant = data?.variant || null;
          this._queueReconcile();
        });
      }
      this._bindForm();
      this._lockControls();
      this._setGate(true);
      this._setStatus('Preparing your options…');
      this._startTimeout();
      this._queueReconcile();
    }
    disconnectedCallback() {
      this._connected = false;
      this.removeEventListener('change', this._onChangeBound, true);
      this._form?.removeEventListener('submit', this._onSubmitBound, true);
      this._observer?.disconnect();
      if (typeof this._unsubscribe === 'function') this._unsubscribe();
      clearTimeout(this._timeout);
      this._unlockControls();
      for (const [gate, state] of this._gateStates || []) {
        gate.inert = state.inert;
        if (state.marker === null) gate.removeAttribute('data-df-combo-blocked');
        else gate.setAttribute('data-df-combo-blocked', state.marker);
      }
      if (this._createdProperty && this._property?.isConnected) this._property.remove();
      this._form = null;
      this._property = null;
      this._createdProperty = false;
      this._unsubscribe = null;
    }
    _sectionId() {
      const native = this.querySelector('variant-selects');
      const id = this.dataset.sectionId || this._scope?.dataset.section || native?.dataset.section;
      return id ? String(id) : '';
    }
    _bindForm() {
      const form = this.dataset.formId ? document.getElementById(this.dataset.formId) : null;
      if (!form || !this._scope?.contains(form)) return false;
      if (form === this._form) return true;
      this._form?.removeEventListener('submit', this._onSubmitBound, true);
      if (this._createdProperty && this._property?.isConnected) this._property.remove();
      this._form = form;
      form.addEventListener('submit', this._onSubmitBound, true);
      this._property = form.querySelector('input[name="properties[Intermediate posts]"]');
      this._createdProperty = !this._property;
      if (!this._property) {
        this._property = document.createElement('input');
        this._property.type = 'hidden';
        this._property.name = 'properties[Intermediate posts]';
        this._property.setAttribute('data-df-post-property', '');
        form.appendChild(this._property);
      }
      return true;
    }
    _nativeValues() {
      const native = this.querySelector('variant-selects');
      if (!native) return null;
      const values = [...native.querySelectorAll('select option:checked, fieldset input:checked')].map(element => element.value);
      return values.length === 3 ? values : null;
    }
    _renderedVariant() {
      const native = this.querySelector('variant-selects');
      const script = native?.querySelector('script[data-selected-variant]') || native?.querySelector('script[type="application/json"]');
      try {
        const variant = script ? JSON.parse(script.textContent) : this._candidateVariant;
        return variant?.id && Array.isArray(variant.options) && variant.options.length === 3 ? variant : null;
      } catch (_) { return null; }
    }
    _agreedSelection() {
      const values = this._nativeValues();
      const variant = this._renderedVariant();
      const formId = this._form?.querySelector('input[name="id"]')?.value;
      if (!values || !variant || String(variant.id) !== String(formId)) return null;
      if (!values.every((value,index) => value === variant.options[index])) return null;
      if (this._pending && !values.every((value,index) => value === this._expected?.[index])) return null;
      const logical = DFComboPosts.logicalValues(values[2]);
      return logical ? { values, variant, logical } : null;
    }
    _isComplete(selection) {
      return Boolean(selection && selection.values[0] && selection.values[1] && selection.values[0] !== 'Select Option' && selection.values[1] !== 'Select Option' && selection.logical.service !== '0' && selection.logical.post !== '0');
    }
    _onChange(event) {
      const target = event.target;
      if (!(target instanceof HTMLInputElement || target instanceof HTMLSelectElement)) return;
      const isLogical = target.matches('[data-df-post], [data-df-service]');
      const isNative = Boolean(target.closest('variant-selects') && this.contains(target));
      if (!isLogical && !isNative) return;
      if (!this._ready || this._pending) { event.preventDefault(); event.stopImmediatePropagation(); return; }
      if (isLogical) {
        const post = this.querySelector('[data-df-post]:checked')?.value;
        const service = this.querySelector('[data-df-service]:checked')?.value;
        const value = DFComboPosts.combinedValue(service, post);
        const select = this.querySelector('variant-selects select[data-df-combined]');
        if (!value || !select || ![...select.options].some(option => option.value === value)) { this._fail(); return; }
        select.value = value;
        // Dawn updates option[selected] and starts its native variant/price request.
        select.dispatchEvent(new Event('change', { bubbles: true }));
        return;
      }
      const values = this._nativeValues();
      if (!values || !DFComboPosts.logicalValues(values[2])) { event.preventDefault(); event.stopImmediatePropagation(); this._fail(); return; }
      this._expected = values;
      this._candidateVariant = null;
      this._pending = true;
      this._setGate(true);
      this._setStatus('Updating price…');
      this._lockControls();
      this._startTimeout();
    }
    _queueReconcile() {
      if (this._reconcileQueued || !this._connected) return;
      this._reconcileQueued = true;
      queueMicrotask(() => { this._reconcileQueued = false; if (this._connected && this.isConnected) this._reconcile(); });
    }
    _reconcile() {
      if (this._fatal) { this._lockControls(); this._setGate(true); return; }
      if (!this._bindForm()) { this._setGate(true); return; }
      const selection = this._agreedSelection();
      if (!selection) { this._setGate(true); if (this._pending || !this._ready) this._lockControls(); return; }
      this._pending = false;
      this._ready = true;
      this._expected = selection.values;
      clearTimeout(this._timeout);
      for (const input of this.querySelectorAll('[data-df-service]')) input.checked = input.value === selection.logical.service;
      for (const input of this.querySelectorAll('[data-df-post]')) input.checked = input.value === selection.logical.post;
      this._updateProperty(selection.logical.post);
      this._unlockControls();
      const complete = this._isComplete(selection);
      this._setGate(!complete);
      this._setStatus(complete ? '' : prompt);
    }
    _updateProperty(post) {
      if (!this._property) return;
      let value = 'Select Option';
      if (post === '1') value = 'No posts needed (existing posts in place)';
      if (post === '2') {
        const size = this.dataset.postSize?.trim();
        value = 'Heavy Duty Concrete Intermediate posts' + (size ? ' (' + size + ')' : '');
      }
      this._property.value = value;
    }
    _onSubmit(event) {
      const selection = this._agreedSelection();
      if (this._ready && !this._pending && this._isComplete(selection)) { this._updateProperty(selection.logical.post); return; }
      event.preventDefault(); event.stopImmediatePropagation();
      this._setGate(true);
      this._setStatus(this._pending ? 'Please wait for the price to update.' : prompt);
    }
    _lockControls() {
      for (const input of this.querySelectorAll('variant-selects select, variant-selects input[type="radio"], [data-df-service], [data-df-post]')) {
        if (!this._lockedControls.has(input)) this._lockedControls.set(input, input.disabled);
        input.disabled = true;
      }
    }
    _unlockControls() { for (const [input, wasDisabled] of this._lockedControls || []) input.disabled = wasDisabled; this._lockedControls?.clear(); }
    _setGate(blocked) {
      const gate = this._form?.querySelector('.product-form__buttons');
      if (!gate) return;
      if (!this._gateStates.has(gate)) this._gateStates.set(gate, { inert: gate.getAttribute('data-df-combo-blocked') === 'true' ? false : gate.inert, marker: gate.getAttribute('data-df-combo-blocked') });
      const baseline = this._gateStates.get(gate);
      gate.inert = blocked || baseline.inert;
      gate.setAttribute('data-df-combo-blocked', blocked ? 'true' : 'false');
    }
    _setStatus(message) {
      const status = this.querySelector('[data-df-combo-status]');
      if (!status) return;
      if (status.textContent !== message) status.textContent = message;
      status.hidden = !message;
    }
    _startTimeout() {
      clearTimeout(this._timeout);
      this._timeout = setTimeout(() => { if (!this._connected || (this._ready && !this._pending)) return; this._setGate(true); this._setStatus(failure); }, 15000);
    }
    _fail() { this._fatal = true; this._ready = false; this._lockControls(); this._setGate(true); this._setStatus(failure); }
  }
  customElements.define('df-combo-posts', DFComboPosts);
})();
